import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';

import { TICKET_TYPE_STANDALONE, TICKET_TYPE_NESTED, TICKET_TYPE_ADD_ON } from 'common/entities/ticketType/selectors';
import {
    getTicketOverlayStyles,
    getTicketBackgroundStyles,
    getTicketBorderStyles,
    getTicketNameTextStyles,
    getTicketDateTimeTextStyles,
    getTicketDescriptionTextStyles,
    getTicketRemainingTextStyles,
    getTicketPriceTextStyles,
    getTicketSoldOutVisibility,
    getTicketQuantityStyles,
    getTicketStylesLayout,
} from 'common/entities/form/styleConfiguration/selectors';
import { getOpenTicketStylingSection, getLimitOneTicketTypePerEmail } from '../../../../selectors';
import { getTicketSelectionQuantityValues } from '../../../../selectors/form';
import { TICKET_VIEW_QA } from '../../../../constants/ticketDataQa';

import {
    TicketWrapperAppearTransition,
    TicketOverlay,
    TicketContainer,
    TicketBackgroundContainer,
    TicketTitle,
    TicketDateAndTime,
    TicketDescription,
    TicketRemaining,
    TicketPriceContainer,
    TicketQuantityContainer,
} from './TicketTypeView.styled';

import TicketTypePrice from './TicketTypePrice';
import TicketTypeQuantity from './TicketTypeQuantity';
import { withFormId } from '../../../FormIdContext';
import { DEFAULT_TICKET_FONT_SIZE } from '../../../../../../entities/form/styleConfiguration/defaultValues';
import SanitizedHTML from '../../../SanitizedHTML';

const SOLD_OUT = 'soldOut';

const TicketTypeView = ({
    category,
    isSoldOut,
    visible,
    soldOutVisibility,
    isStylingOverlay,
    ticketTypeStyles,
    ...props
}) => (
    <TicketWrapperAppearTransition in={!(!soldOutVisibility && isSoldOut) && visible} category={category}>
        <TicketContainer
            data-qa={`${TICKET_VIEW_QA}__container`}
            borderColor={get(ticketTypeStyles, 'border.colors.default.value')}
            borderColorHover={get(ticketTypeStyles, 'border.colors.hover.value')}
            borderWidths={get(ticketTypeStyles, 'border.sizes')}
            borderRadius={get(ticketTypeStyles, 'border.radius')}
            ticketStyle={get(ticketTypeStyles, 'ticketStyle')}
        >
            <TicketBackgroundContainer
                backgroundColor={get(ticketTypeStyles, 'background.backgroundColor.value')}
                opacity={get(ticketTypeStyles, 'background.opacity')}
                imageUrl={get(ticketTypeStyles, 'background.imageUrl')}
                borderRadius={get(ticketTypeStyles, 'border.radius')}
            />
            {(isSoldOut || isStylingOverlay) && (
                <TicketOverlay
                    data-qa={`${TICKET_VIEW_QA}__overlay`}
                    backgroundColor={get(ticketTypeStyles, 'overlay.color.value')}
                    opacity={get(ticketTypeStyles, 'overlay.opacity')}
                    imageUrl={get(ticketTypeStyles, 'overlay.imageUrl')}
                />
            )}

            <TicketTitle
                data-qa={`${TICKET_VIEW_QA}__title`}
                fontFamily={get(ticketTypeStyles, 'name.font.value')}
                color={get(ticketTypeStyles, 'name.color.value')}
                /* This is needed as an inline style in order to have higher specificity
                 * in the guest view when the form is inside a modal. [SWP-30206] for reference
                 */
                style={{
                    fontSize: `${get(ticketTypeStyles, 'name.fontSize') || DEFAULT_TICKET_FONT_SIZE}px`,
                }}
            >
                {props.label}
            </TicketTitle>
            {!!(props.startDateTime || props.endDateTime) && (
                <TicketDateAndTime
                    data-qa={`${TICKET_VIEW_QA}__dateTime`}
                    fontFamily={get(ticketTypeStyles, 'dateTime.font.value')}
                    fontSize={get(ticketTypeStyles, 'dateTime.fontSize')}
                    color={get(ticketTypeStyles, 'dateTime.color.value')}
                >
                    {props.startDateTime && <p>Start: {props.startDateTime}</p>}
                    {!!props.endDateTime && <p>End: {props.endDateTime}</p>}
                </TicketDateAndTime>
            )}
            {props.description && (
                <TicketDescription
                    data-qa={`${TICKET_VIEW_QA}__description`}
                    fontFamily={get(ticketTypeStyles, 'description.font.value')}
                    fontSize={get(ticketTypeStyles, 'description.fontSize')}
                    color={get(ticketTypeStyles, 'description.color.value')}
                >
                    <SanitizedHTML innerHtml={props.description} />
                </TicketDescription>
            )}
            {props.showRemaining && (
                <TicketRemaining
                    data-qa={`${TICKET_VIEW_QA}__remaining`}
                    fontFamily={get(ticketTypeStyles, 'remaining.font.value')}
                    fontSize={get(ticketTypeStyles, 'remaining.fontSize')}
                    color={get(ticketTypeStyles, 'remaining.color.value')}
                >
                    {`${props.quantityRemaining}/${props.quantity} remaining`}
                </TicketRemaining>
            )}

            <TicketPriceContainer ticketStyle={get(ticketTypeStyles, 'ticketStyle')}>
                <TicketTypePrice
                    qaId={TICKET_VIEW_QA}
                    priceStyles={get(ticketTypeStyles, 'price')}
                    quantityStyles={get(ticketTypeStyles, 'quantity')}
                    {...props}
                />
            </TicketPriceContainer>
            <TicketQuantityContainer ticketStyle={get(ticketTypeStyles, 'ticketStyle')}>
                <TicketTypeQuantity
                    qaId={TICKET_VIEW_QA}
                    quantityStyles={get(ticketTypeStyles, 'quantity')}
                    isSoldOut={isSoldOut}
                    ticketStyle={get(ticketTypeStyles, 'ticketStyle')}
                    {...props}
                />
            </TicketQuantityContainer>
        </TicketContainer>
    </TicketWrapperAppearTransition>
);

TicketTypeView.propTypes = {
    category: PropTypes.oneOf([TICKET_TYPE_STANDALONE, TICKET_TYPE_NESTED, TICKET_TYPE_ADD_ON]),
    visible: PropTypes.bool.isRequired,
    isSoldOut: PropTypes.bool,
    soldOutVisibility: PropTypes.bool,
    isStylingOverlay: PropTypes.bool.isRequired,
    ticketTypeStyles: PropTypes.shape({
        overlay: PropTypes.object,
        quantity: PropTypes.object,
        background: PropTypes.object,
        border: PropTypes.object,
        name: PropTypes.object,
        dateTime: PropTypes.object,
        description: PropTypes.object,
        remaining: PropTypes.object,
        price: PropTypes.object,
    }).isRequired,
    label: PropTypes.string,
    description: PropTypes.string,
    startDateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    endDateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    quantity: PropTypes.number,
    quantityRemaining: PropTypes.number,
    showRemaining: PropTypes.bool,
};

TicketTypeView.defaultProps = {
    category: TICKET_TYPE_STANDALONE,
    isSoldOut: false,
    soldOutVisibility: true,
    label: null,
    description: null,
    startDateTime: null,
    endDateTime: null,
    quantity: null,
    quantityRemaining: null,
    price: null,
    showRemaining: true,
};

const mapStateToProps = (state, { category, formId, id, isSoldOut }) => {
    const openStylingSection = getOpenTicketStylingSection(state);
    const isStylingOverlay = openStylingSection === SOLD_OUT;

    const ticketOverlayStyles = getTicketOverlayStyles(state, formId, category);
    const soldOutVisibility = getTicketSoldOutVisibility(state, formId, category);
    const ticketQuantityStyles = getTicketQuantityStyles(state, formId, category);

    const ticketBackgroundStyles = getTicketBackgroundStyles(state, formId, category);
    const ticketBorderStyles = getTicketBorderStyles(state, formId, category);

    const ticketNameTextStyles = getTicketNameTextStyles(state, formId, category);
    const ticketDateTimeTextStyles = getTicketDateTimeTextStyles(state, formId, category);
    const ticketDescriptionTextStyles = getTicketDescriptionTextStyles(state, formId, category);
    const ticketRemainingTextStyles = getTicketRemainingTextStyles(state, formId, category);
    const ticketPriceTextStyles = getTicketPriceTextStyles(state, formId, category);
    const ticketStyle = getTicketStylesLayout(state, formId, category) || {};

    const limitOneTicketTypePerEmail = getLimitOneTicketTypePerEmail(state);
    const quantities = limitOneTicketTypePerEmail ? getTicketSelectionQuantityValues(state) : [];
    const quantityInOtherTickets = Object.keys(quantities)
        .filter((ticketTypeId) => ticketTypeId !== id) // exclude current ticket.
        .reduce((memo, ticketTypeId) => memo + (quantities[ticketTypeId] || 0), 0);
    const isDisabledByOneTicketTypePerEmail = quantityInOtherTickets > 0;

    const disabled = isSoldOut || isDisabledByOneTicketTypePerEmail;

    return {
        formId,
        ticketTypeStyles: {
            overlay: { ...(ticketOverlayStyles || {}) },
            quantity: { ...(ticketQuantityStyles || {}) },
            background: { ...(ticketBackgroundStyles || {}) },
            border: { ...(ticketBorderStyles || {}) },
            name: { ...(ticketNameTextStyles || {}) },
            dateTime: { ...(ticketDateTimeTextStyles || {}) },
            description: { ...(ticketDescriptionTextStyles || {}) },
            remaining: { ...(ticketRemainingTextStyles || {}) },
            price: { ...(ticketPriceTextStyles || {}) },
            ticketStyle,
        },
        disabled,
        soldOutVisibility,
        isStylingOverlay,
    };
};

export default withFormId(connect(mapStateToProps, {})(TicketTypeView));
